/**
 * Вкладка «Разделы» каталога программ обучения (component.team-training-programs.directions,
 * centrika-docs!55; training-catalog-v2 — дерево разделов, ТЗ 26.10.2026, п.4.6). Разделы до трёх
 * уровней: добавление (в корень и подразделом), переименование, порядок «выше/ниже» в своём
 * уровне, удаление пустого раздела. «Колонка журнала» — в какую колонку журнала сроков клиента
 * попадают программы раздела и его подразделов; у раздела без колонки — по ближайшему разделу
 * выше, иначе «Прочее». Раздел с колонкой журнала не удаляется (сначала перенесите колонку).
 * Правит ТОЛЬКО админ.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from 'lucide-react'
import {
  adminApi,
  trainingApi,
  ApiError,
  type TrainingDirection,
  type TrainingDirectionItem,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { CATALOG_MAX_DEPTH, JOURNAL_COLUMN_OPTIONS, flattenDirections, type DirectionNode } from '@/lib/training-catalog'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { cn } from '@/lib/utils'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** Значение «без колонки» для Select. */
const NO_COLUMN = '__none__'


function NameForm({
  label,
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  label: string
  initial: string
  submitLabel: string
  busy: boolean
  onSubmit: (name: string) => void
  onCancel?: () => void
}) {
  const [name, setName] = useState(initial)
  function submit(e: FormEvent) {
    e.preventDefault()
    if (name.trim() && !busy) onSubmit(name.trim())
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-1 flex-wrap items-end gap-3">
      <Field label={label} required className="my-0 min-w-56 flex-1">
        {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!name.trim() || busy}>
          {busy ? 'Сохранение…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onCancel}
            leftIcon={<X className="size-4 stroke-current" aria-hidden />}
          >
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  )
}

export function DirectionsTab() {
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])
  // Действующие программы — для числа программ раздела (с подразделами): пустые разделы
  // (например, прежние, где остались только архивные программы) приглушены.
  const [activeByDirection, setActiveByDirection] = useState<Map<string, number>>(() => new Map())
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [creating, setCreating] = useState(false)
  // Форма под разделом: правка названия или новый подраздел.
  const [editId, setEditId] = useState<string | null>(null)
  const [childOf, setChildOf] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const nodes = useMemo(() => flattenDirections(directions), [directions])
  // Число действующих программ раздела вместе с подразделами: программа добавляется своему
  // разделу и всем разделам выше.
  const totals = useMemo(() => {
    const parentOf = new Map(nodes.map((n) => [n.direction.id, n.direction.parentId]))
    const out = new Map<string, number>()
    for (const [directionId, count] of activeByDirection) {
      let cur: string | undefined = directionId
      for (let i = 0; cur && i < 10; i++) {
        out.set(cur, (out.get(cur) ?? 0) + count)
        cur = parentOf.get(cur) || undefined
      }
    }
    return out
  }, [nodes, activeByDirection])
  const siblings = useMemo(() => {
    const byParent = new Map<string, string[]>()
    for (const n of nodes) {
      const key = n.direction.parentId
      byParent.set(key, [...(byParent.get(key) ?? []), n.direction.id])
    }
    return byParent
  }, [nodes])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setLoadError(null)
    Promise.all([trainingApi.listDirections(controller.signal), trainingApi.listPrograms(controller.signal)])
      .then(([dirs, programs]) => {
        if (controller.signal.aborted) return
        setDirections(dirs)
        const counts = new Map<string, number>()
        for (const p of programs) {
          if (!p.archived && p.status === 'active') counts.set(p.directionId, (counts.get(p.directionId) ?? 0) + 1)
        }
        setActiveByDirection(counts)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setLoadError(getErrorMessage(err, 'Не удалось загрузить разделы'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  async function create(name: string, parentId?: string) {
    setActionError(null)
    setCreating(true)
    try {
      const created = await adminApi.training.createDirection(name, parentId)
      setDirections((prev) => [...prev, created])
      setChildOf(null)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось создать раздел'))
    } finally {
      setCreating(false)
    }
  }

  async function rename(id: string, name: string) {
    setActionError(null)
    setBusyId(id)
    try {
      const updated = await adminApi.training.updateDirection(id, name)
      setDirections((prev) => prev.map((d) => (d.id === id ? { ...d, ...updated } : d)))
      setEditId(null)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось сохранить раздел'))
    } finally {
      setBusyId(null)
    }
  }

  async function run(id: string, action: () => Promise<TrainingDirectionItem[]>, fallback: string) {
    setActionError(null)
    setBusyId(id)
    try {
      setDirections(await action())
    } catch (err) {
      setActionError(getErrorMessage(err, fallback))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(d: TrainingDirectionItem) {
    setActionError(null)
    setBusyId(d.id)
    try {
      await adminApi.training.deleteDirection(d.id)
      setDirections((prev) => prev.filter((x) => x.id !== d.id))
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError(getErrorMessage(err, `Раздел «${d.name}» используется — удалить нельзя.`))
      } else {
        setActionError(getErrorMessage(err, 'Не удалось удалить раздел'))
      }
    } finally {
      setBusyId(null)
    }
  }

  function renderNode(n: DirectionNode) {
    const d = n.direction
    const level = siblings.get(d.parentId) ?? []
    const pos = level.indexOf(d.id)
    const busy = busyId === d.id
    const indent = { paddingLeft: `${16 + (n.depth - 1) * 24}px` }
    return (
      <li key={d.id} className="border-b border-line last:border-b-0">
        <div className="flex flex-wrap items-center gap-2 py-3 pr-4" style={indent}>
          {editId === d.id ? (
            <NameForm
              label="Название раздела"
              initial={d.name}
              submitLabel="Сохранить"
              busy={busy}
              onSubmit={(name) => void rename(d.id, name)}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <>
              <div className="min-w-0 flex-1">
                <span
                  className={cn(
                    'break-words',
                    n.depth === 1 ? 'font-semibold' : '',
                    (totals.get(d.id) ?? 0) > 0 ? 'text-txt' : 'text-txt-tertiary',
                  )}
                >
                  {d.name}
                </span>
                <span className="ml-2 text-12 text-txt-tertiary">
                  {(totals.get(d.id) ?? 0) > 0 ? `программ: ${totals.get(d.id)}` : 'нет действующих программ'}
                </span>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Раздел «${d.name}» выше`}
                  disabled={busy || pos <= 0}
                  onClick={() => void run(d.id, () => adminApi.training.moveDirection(d.id, true), 'Не удалось переместить раздел')}
                  leftIcon={<ArrowUp className="size-4 stroke-current" aria-hidden />}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Раздел «${d.name}» ниже`}
                  disabled={busy || pos < 0 || pos >= level.length - 1}
                  onClick={() => void run(d.id, () => adminApi.training.moveDirection(d.id, false), 'Не удалось переместить раздел')}
                  leftIcon={<ArrowDown className="size-4 stroke-current" aria-hidden />}
                />
                <Select
                  value={d.systemCode || NO_COLUMN}
                  onValueChange={(v) =>
                    void run(
                      d.id,
                      () => adminApi.training.setDirectionJournalCode(d.id, v === NO_COLUMN ? '' : (v as TrainingDirection)),
                      'Не удалось назначить колонку журнала',
                    )
                  }
                  disabled={busy}
                >
                  <SelectTrigger aria-label={`Колонка журнала раздела «${d.name}»`} className="h-8 w-auto min-w-44 text-13">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_COLUMN}>Колонка — по разделу выше</SelectItem>
                    {JOURNAL_COLUMN_OPTIONS.map((o) => (
                      <SelectItem key={o.code} value={o.code}>
                        Колонка «{o.label}»
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label="Править"
                  title="Переименовать"
                  onClick={() => setEditId(d.id)}
                  leftIcon={<Pencil className="size-4 stroke-current" aria-hidden />}
                />
                {n.depth < CATALOG_MAX_DEPTH ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label="Подраздел"
                    title="Добавить подраздел"
                    onClick={() => setChildOf(d.id)}
                    leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                  />
                ) : null}
                {!d.system ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void remove(d)}
                    aria-label={`Удалить раздел «${d.name}»`}
                    title="Удалить"
                    leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
        {childOf === d.id ? (
          <div className="flex flex-wrap items-end gap-3 pb-3 pr-4" style={{ paddingLeft: `${16 + n.depth * 24}px` }}>
            <NameForm
              label={`Подраздел раздела «${d.name}»`}
              initial=""
              submitLabel="Добавить подраздел"
              busy={creating}
              onSubmit={(name) => void create(name, d.id)}
              onCancel={() => setChildOf(null)}
            />
          </div>
        ) : null}
      </li>
    )
  }

  return (
    <div>
      <Card header="Новый раздел" className="mb-4">
        <NameForm
          key={directions.length}
          label="Название раздела"
          initial=""
          submitLabel="Добавить раздел"
          busy={creating}
          onSubmit={(name) => void create(name)}
        />
      </Card>

      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}

      {loadError ? (
        <Alert variant="danger">{loadError}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <Panel>
          <ul>{nodes.map(renderNode)}</ul>
        </Panel>
      )}
    </div>
  )
}
